import { useBudgetContext } from '@/components/cashflow/budget/BudgetContext';
import { getStatusColors } from '@/components/cashflow/budget/getStatusColors';
import { FlexCol } from '@/components/common/Flex';
import { Text } from '@/components/common/Typography';
import { Skeleton } from '@chakra-ui/react';
import { BudgetPurpose } from '@common/graphql/generated';
import {
    getBudgetPurposeActionLabel,
    getBudgetPurposeUnderLabel,
} from '@common/react/budget';
import { toDollars } from '@common/util/currency';
import { dt } from '@common/util/dateTime';
import { toPercentageStr } from '@common/util/numbers';

interface BudgetAmountStatusProps {
    budgetedAmount: number;
    budgetPurpose: BudgetPurpose;
    amountLeft: number;
    percentage: number;
    withPrefix?: boolean;
    loading?: boolean;
}

export const BudgetAmountStatus = ({
    budgetedAmount,
    budgetPurpose,
    amountLeft,
    percentage,
    withPrefix = false,
    loading = false,
}: BudgetAmountStatusProps) => {
    const { dateRange } = useBudgetContext();
    const isPastDate = dateRange.endDate < dt().toDateStr();
    const isOver = amountLeft < 0;
    const colors = getStatusColors({ budgetPurpose, amountLeft });
    const actionLabel = getBudgetPurposeActionLabel(budgetPurpose);
    const subject = isPastDate ? 'You' : "You've";

    if (loading) {
        return (
            <FlexCol gap={1}>
                <Skeleton h={5} w={100} />
                <Skeleton h={5} w={200} />
            </FlexCol>
        );
    }

    return (
        <FlexCol gap={1}>
            {budgetedAmount === 0 ? (
                <Text fontSize="xl" fontWeight={600}>
                    No budget set
                </Text>
            ) : amountLeft === 0 ? (
                <Text fontSize="xl" fontWeight={600} color="green.800">
                    On track
                </Text>
            ) : (
                <Text fontSize="xl" fontWeight={600} color={colors.text}>
                    {toDollars(Math.abs(amountLeft))}{' '}
                    <Text fontSize="xl" fontWeight={600} as="span" color="text">
                        {isOver
                            ? 'over'
                            : getBudgetPurposeUnderLabel(budgetPurpose)}
                    </Text>
                </Text>
            )}

            {budgetedAmount > 0 && (
                <Text color="textMuted">
                    {amountLeft === 0
                        ? withPrefix
                            ? `${subject} ${actionLabel} exactly as much budget`
                            : 'Exactly as much as budgeted'
                        : `${withPrefix ? `${subject} ${actionLabel} ` : ''}${toPercentageStr(percentage, 1)} ${isOver ? 'more' : 'less'} than budgeted`}
                </Text>
            )}
        </FlexCol>
    );
};
